import { Button } from '@workspace/ui/components/button'
import { useState } from 'react'
import { useInvestorConfigQuery, useStartupConfigQuery } from '../../../hooks/api'
import type { FormState, InfoSection } from './confirm-form-utils'
import { parseFormState, serializeFounderForm, serializeInvestorForm } from './confirm-form-utils'
import { FounderConfirmForm } from './founder-confirm-form'
import { InvestorConfirmForm } from './investor-confirm-form'

interface ConfirmInfoCardProps {
  items?: { label: string; value: string }[]
  sections?: InfoSection[]
  resolved?: boolean
  onConfirm: (editedSections?: InfoSection[]) => void
}

export function ConfirmInfoCard({
  items,
  sections: initialSections,
  resolved,
  onConfirm,
}: ConfirmInfoCardProps) {
  const { data: configResponse } = useStartupConfigQuery()
  const { data: investorConfigResponse } = useInvestorConfigQuery()

  const categories = configResponse?.data?.categories ?? []
  const productStages = configResponse?.data?.productStages ?? []
  const allTags = (investorConfigResponse?.data?.investmentAreas ?? []).map((a) => a.label)
  const investmentAreaOptions = (investorConfigResponse?.data?.investmentAreas ?? []).map((a) => ({
    value: a.value,
    label: a.label,
  }))
  const investmentRegionOptions = (investorConfigResponse?.data?.investmentRegions ?? []).map(
    (r) => ({ value: r.value, label: r.label })
  )

  const [formState, setFormState] = useState<FormState>(() => {
    const raw = initialSections ?? (items ? [{ title: '', items }] : [])
    return parseFormState(raw)
  })

  const handleConfirm = () => {
    const serialized =
      formState.role === 'founder'
        ? serializeFounderForm(formState.data)
        : serializeInvestorForm(formState.data)
    onConfirm(serialized)
  }

  return (
    <div className="min-w-[220px] overflow-hidden rounded-xl border border-border bg-card">
      <div className="divide-y divide-border">
        {formState.role === 'founder' ? (
          <FounderConfirmForm
            data={formState.data}
            disabled={resolved}
            categories={categories}
            productStages={productStages}
            allTags={allTags}
            onChange={(data) => setFormState({ role: 'founder', data })}
          />
        ) : (
          <InvestorConfirmForm
            data={formState.data}
            disabled={resolved}
            investmentAreaOptions={investmentAreaOptions}
            investmentRegionOptions={investmentRegionOptions}
            onChange={(data) => setFormState({ role: 'investor', data })}
          />
        )}
      </div>
      {!resolved && (
        <div className="border-t border-border p-3">
          <Button onClick={handleConfirm} size="sm" className="w-full">
            Confirm
          </Button>
        </div>
      )}
    </div>
  )
}
